import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { apiPost } from "../../shared/apiClient";
import { deleteCurrency, fetchCurrenciesList } from "../../shared/geographyMastersApi";
import { FormModalShell } from "../../shared/FormModalShell";
import { IconClose, IconPlus, IconSave } from "../../shared/toolbarIcons";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

const dataGridSx = {
  border: "none",
  fontFamily: "inherit",
  fontSize: "0.9rem",
  height: "100%",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f2fcf5",
    borderBottom: "1px solid #e8ecea",
    fontWeight: 600
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #e8ecea",
    backgroundColor: "#fbfffc"
  },
  "& .MuiDataGrid-row:nth-of-type(even):not(.MuiDataGrid-row--editing):not(:hover)": {
    backgroundColor: "#fcfffd"
  },
  "& .MuiDataGrid-cell": {
    borderBottomColor: "#e8ecea"
  }
};

const emptyForm = () => ({
  name: "",
  symbol: "",
  code: "",
  isActive: true
});

export function CurrenciesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm());
  const [formError, setFormError] = useState(null);

  const rows = useMemo(
    () =>
      items.map((c) => ({
        id: c.id,
        currency: c
      })),
    [items]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await fetchCurrenciesList();
    if (!res.ok) {
      setError(res.error ?? "Failed to load data");
      setItems([]);
    } else {
      setItems(res.items);
    }
    setLoading(false);
  }, []);

  const handleDeleteClick = useCallback(
    async (id) => {
      if (!(await window.confirmDialog("Delete this currency?"))) return;
      const res = await deleteCurrency(id);
      if (!res.ok) setError(res.error ?? "Failed to delete currency");
      else {
        if (editingId === id) {
          setIsModalOpen(false);
          setEditingId(null);
          setForm(emptyForm());
          setFormError(null);
        }
        await load();
      }
    },
    [editingId, load]
  );

  const openCreateModal = useCallback(() => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setIsModalOpen(true);
  }, []);

  const openEditModal = useCallback((currency) => {
    setEditingId(currency.id);
    setForm({
      name: currency.name ?? "",
      symbol: currency.symbol ?? "",
      code: currency.code ?? "",
      isActive: Boolean(currency.isActive)
    });
    setFormError(null);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setFormError("Currency name is required.");
      return;
    }

    setSaving(true);
    setFormError(null);

    const res = await apiPost("/api/Currencies", {
      id: editingId ?? 0,
      name: form.name.trim(),
      symbol: form.symbol,
      code: form.code,
      isActive: form.isActive
    });

    if (!res.ok) {
      setFormError(res.error ?? "Failed to save currency");
      setSaving(false);
      return;
    }

    closeModal();
    await load();
    setSaving(false);
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 140,
        valueGetter: (_v, row) => row.currency.name ?? "-"
      },
      {
        field: "code",
        headerName: "Code",
        flex: 0.7,
        minWidth: 90,
        valueGetter: (_v, row) => row.currency.code ?? "-"
      },
      {
        field: "symbol",
        headerName: "Symbol",
        width: 100,
        valueGetter: (_v, row) => row.currency.symbol ?? "-"
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const c = params.row.currency;
          return (
            <div className="table-actions">
              <button className="icon-button" type="button" title="Edit" onClick={() => openEditModal(c)}>
                ✏️
              </button>
              <button className="icon-button danger" type="button" title="Delete" onClick={() => void handleDeleteClick(c.id)}>
                🗑️
              </button>
            </div>
          );
        }
      }
    ],
    [handleDeleteClick, openEditModal]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.length;

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Currencies</h2>
          <p>{loading ? "Loading currencies..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <button className="icon-button icon-button--toolbar" type="button" title="Add currency" aria-label="Add currency" onClick={openCreateModal}>
            <IconPlus />
          </button>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={dataGridSx}
          />
        </div>
      </div>
      <FormModalShell
        open={isModalOpen}
        title={editingId ? "Edit Currency" : "Add Currency"}
        subtitle="Manage currency details."
        onClose={closeModal}
        actions={
          <>
            <button
              type="button"
              className="icon-button icon-button--toolbar"
              onClick={closeModal}
              disabled={saving}
              title="Cancel"
              aria-label="Cancel"
            >
              <IconClose />
            </button>
            <button
              type="submit"
              form="currency-modal-form"
              className="icon-button icon-button--toolbar"
              disabled={saving}
              title={saving ? "Saving" : "Save"}
              aria-label={saving ? "Saving" : "Save"}
            >
              <IconSave />
            </button>
          </>
        }
      >
        <form id="currency-modal-form" onSubmit={handleSubmit} className="form form-fields-bottomline">
          <div className="form-row form-row--4">
            <label className="field">
              <span>Name</span>
              <input type="text" value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} />
            </label>
            <label className="field">
              <span>Code</span>
              <input type="text" value={form.code} onChange={(event) => setForm((prev) => ({ ...prev, code: event.target.value }))} />
            </label>
            <label className="field">
              <span>Symbol</span>
              <input type="text" value={form.symbol} onChange={(event) => setForm((prev) => ({ ...prev, symbol: event.target.value }))} />
            </label>
          </div>
          {formError && <div className="error-banner">{formError}</div>}
        </form>
      </FormModalShell>
    </div>
  );
}
